import { m } from 'framer-motion';
import { useCallback, useEffect, useState } from 'react';
// @mui
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
// hooks
import { enqueueSnackbar } from 'notistack';
import { useBoolean } from 'src/hooks/use-boolean';
import { useResponsive } from 'src/hooks/use-responsive';
// _mock
// components
import { supabase } from 'src/api/supabase';
import { useAuthContext } from 'src/auth/hooks';
import { varHover } from 'src/components/animate';
import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';
//
import NotificationItem from './notification-item';

// ----------------------------------------------------------------------

const TABS = [
  {
    value: 'all',
    label: 'All',
    count: 22,
  },
];

// ----------------------------------------------------------------------

export default function NotificationsPopover() {
  const drawer = useBoolean();
  const { user } = useAuthContext();
  const smUp = useResponsive('up', 'sm');

  const [currentTab, setCurrentTab] = useState('all');

  const handleChangeTab = useCallback((event, newValue) => {
    setCurrentTab(newValue);
  }, []);

  const [notifications, setNotifications] = useState([]);

  const totalUnRead = notifications.filter((item) => item.isUnRead === true).length;

  useEffect(() => {
    getAllNotifications();
  }, []);

  async function getAllNotifications() {
    // where approved is false
    const { data: pointreqview, error } = await supabase
      .from('pointreqview')
      .select('*')
      .eq('approved', false);

    if (error) {
      console.log(error);
    } else {
      console.log(pointreqview);
      setNotifications(pointreqview);
    }
  }

  const addHousePoints = async (studentIds, points) => {
    const { data: teachers, error: terror } = await supabase
      .from('teachers')
      .select('id')
      .eq('email', user.email);

    if (terror) {
      enqueueSnackbar(terror.message, { variant: 'error' });
    } else {
      const row = studentIds.map((id) => ({
        student: id,
        amount: parseInt(points, 10),
        teacher: teachers,
        postcard: false,
      }));
      console.log(row);
      const { data, error } = await supabase.from('points').insert(row).select();

      if (error) {
        enqueueSnackbar(error.message, { variant: 'error' });
      }
      if (data) {
        enqueueSnackbar('Points added', { variant: 'success' });
      }
    }
  };

  const updateNotification = async (req, approved) => {
    console.log(req);
    if (approved) {
      const { data, error } = await supabase
        .from('pointrequest')
        .update({ approved: true })
        .eq('id', req.request_id);
      if (error) {
        console.log(error);
      } else {
        console.log(data);
        addHousePoints([req.student_id], req.amount);
      }
      getAllNotifications();
    } else {
      const { error } = await supabase.from('pointrequest').delete().eq('id', req.request_id);
      if (error) {
        console.log(error);
      }
      getAllNotifications();
    }
  };

  const handleMarkAllAsRead = () => {
    setNotifications(
      notifications.map((notification) => ({
        ...notification,
        isUnRead: false,
      }))
    );
  };

  const renderHead = (
    <Stack direction="row" alignItems="center" sx={{ py: 2, pl: 2.5, pr: 1, minHeight: 68 }}>
      <Typography variant="h6" sx={{ flexGrow: 1 }}>
        Notifications
      </Typography>

      {!!totalUnRead && (
        <Tooltip title="Mark all as read">
          <IconButton color="primary" onClick={handleMarkAllAsRead}>
            <Iconify icon="eva:done-all-fill" />
          </IconButton>
        </Tooltip>
      )}

      {!smUp && (
        <IconButton onClick={drawer.onFalse}>
          <Iconify icon="mingcute:close-line" />
        </IconButton>
      )}
    </Stack>
  );

  const renderTabs = (
    <Tabs value={currentTab} onChange={handleChangeTab}>
      {TABS.map((tab) => (
        <Tab
          key={tab.value}
          iconPosition="end"
          value={tab.value}
          label={tab.label}
          sx={{
            '&:not(:last-of-type)': {},
          }}
        />
      ))}
    </Tabs>
  );

  const renderList = (
    <Scrollbar>
      <List disablePadding>
        {notifications.map((notification) => (
          <NotificationItem
            func={updateNotification}
            key={notification.id}
            notification={notification}
          />
        ))}
      </List>
    </Scrollbar>
  );

  return (
    <>
      <IconButton
        component={m.button}
        whileTap="tap"
        whileHover="hover"
        variants={varHover(1.05)}
        color={drawer.value ? 'primary' : 'default'}
        onClick={drawer.onTrue}
      >
        <Badge badgeContent={totalUnRead} color="error">
          <Iconify icon="solar:bell-bing-bold-duotone" width={24} />
        </Badge>
      </IconButton>

      <Drawer
        open={drawer.value}
        onClose={drawer.onFalse}
        anchor="right"
        slotProps={{
          backdrop: { invisible: true },
        }}
        PaperProps={{
          sx: { width: 1, maxWidth: 420 },
        }}
      >
        {renderHead}

        <Divider />

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ pl: 2.5, pr: 1 }}
        >
          {renderTabs}
          <IconButton onClick={handleMarkAllAsRead}>
            <Iconify icon="solar:settings-bold-duotone" />
          </IconButton>
        </Stack>

        <Divider />

        {renderList}

        <Box sx={{ p: 1 }}>
          <Button fullWidth size="large">
            View All
          </Button>
        </Box>
      </Drawer>
    </>
  );
}
